"use client";

import Chevron from "@/app/assets/images/svg/chevron.svg";
import { useScopedI18n } from "@/i18n/client";
import clsx from "clsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { FC, useMemo } from "react";
import ReactPaginate from "react-paginate";
import styles from "./Pagination.module.scss";

interface Props {
	initialPage?: number;
	page?: number;
	pageCount?: number;
	onPageChange?: (page: number) => void;
	className?: string;
	routing?: "window" | "next";
}

const Pagination: FC<Props> = ({
	initialPage,
	page = 0,
	pageCount,
	onPageChange,
	className = "",
	routing = "window",
}) => {
	// const isBot = useIsBot();
	const scopedT = useScopedI18n("text");

	const validPageNumberMinMax = (page: number) => {
		if (!pageCount) {
			return 1;
		}

		// check for page is greater than total pages
		let validPage = page > pageCount ? pageCount : page;

		//check to if page is less than 1 (the lowest)
		validPage = validPage < 1 ? 1 : Math.ceil(validPage);

		return validPage;
	};

	pageCount = !pageCount ? pageCount : Math.ceil(pageCount);

	const pathname = usePathname();
	const params = useSearchParams();
	const router = useRouter();

	const updatePageParam = (page: number) => {
		const updatedParams = new URLSearchParams(params.toString());
		updatedParams.set("page", `${page + 1}`);

		// don't include page param on first page
		if (page === 0) {
			updatedParams.delete("page");
		}

		const paramsString = updatedParams.toString();

		const href = `${pathname}${paramsString ? `?${paramsString}` : ""}`;

		if (routing === "window") {
			return window.history.replaceState("", "", href);
		}

		if (routing === "next") {
			return router.push(href);
		}
	};

	const initialPageMemo = useMemo(() => {
		if (!pageCount) {
			return undefined;
		}

		const pageParam = params.get("page");
		const page = initialPage ? initialPage : Number(pageParam || 1);

		// check for page is greater than total pages
		let validPage = page > pageCount ? pageCount : page;

		//check to if page is less than 1 (the lowest)
		validPage = validPage < 1 ? 1 : validPage;

		validPage = validPage - 1;

		// update page param is it doesn't match
		if (validPage !== page - 1) {
			updatePageParam(validPage);
		}

		return validPage;
	}, [pageCount]);

	const handlePageChange = (page: number) => {
		updatePageParam(page);
		onPageChange?.(page);
	};

	if (!pageCount) {
		return null;
	}

	return (
		<nav className={clsx([className, styles.outer])} aria-label='pagination'>
			<ReactPaginate
				initialPage={initialPageMemo}
				previousLabel={<Chevron aria-labelledby='directional chevron' />}
				nextLabel={<Chevron aria-labelledby='directional chevron' />}
				pageCount={pageCount}
				onPageChange={({ selected }) => handlePageChange(selected)}
				containerClassName={styles.container}
				previousClassName={styles.previous}
				pageLinkClassName={styles.item}
				nextClassName={styles.next}
				disabledClassName={styles.disabled}
				activeClassName={styles.active}
				breakLabel={"..."}
				breakClassName={styles.break}
				pageRangeDisplayed={5}
				marginPagesDisplayed={1}
			/>
		</nav>
	);
};

export default Pagination;
